import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, EventEmitter, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { InputSearchDatePeriod } from '../../../shared/components/input/input.search.datePeriod';
import { Destination } from '../../../shared/models/destination.model';
import { VOICE_TEST_STATUSES } from '../../../shared/models/test-group.model';
import { AuthUser, BasicUser, Role } from '../../../shared/models/user.model';
import { CustomUtils } from '../../../shared/services/custom-utils';
import { ExportService } from '../../../shared/services/export.service';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { DialogRef, ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { UsersService } from '../../../shared/services/users.service';
import { ValidationService, Validators as Vld } from '../../../shared/services/validation.service';
import { SearchParams } from '../../../shared/services/voice-testing.service';
import { UsersSelectComponent } from '../../../users/users-select/users-select.component';
import { DestinationsSelectComponent } from '../../destinations-select/destinations-select.component';

interface VtSearchModel {
  testIds: string;
  testGroupIds: string;
  callerIds: string;
  statuses: string[];
  networks: Destination[];
  userIds: number[];
  users: BasicUser[];
  createdAtFrom: string;
  createdAtTo: string;
}

@Component({
  standalone: false,
  selector: 'app-vt-search-form',
  templateUrl: 'vt-search-form.component.html',
  styleUrls: ['vt-search-form.component.scss'],
})
export class VtSearchFormComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<SearchParams>();

  @ViewChild(DestinationsSelectComponent, { static: false }) countries: DestinationsSelectComponent;
  @ViewChild(UsersSelectComponent, { static: false }) users: UsersSelectComponent;
  @ViewChild(InputSearchDatePeriod, { static: false }) datePeriod: InputSearchDatePeriod;

  @ViewChild('filterTopBar', { static: false }) filterTopBar: ElementRef;
  @ViewChild('usersModalTpl', { read: TemplateRef, static: false }) usersModalTpl: any;

  usersCollection = [];
  showUsers = false;
  onlyMyTests = false;
  saveUserIds = false;
  isBeingDragged = false;

  @ViewChild('exportModalTpl', { read: TemplateRef, static: false }) exportModalTpl: any;
  exportSize: string | number = 'Custom';
  customExportSize = 1000;
  exportSizes = ['Custom', 'All'];
  exportData = {
    url: undefined,
    size: undefined,
    sizeKb: undefined,
  };
  exportSpinner = false;

  form: FormGroup;

  receiptStatuses = [];

  filters = [
    { filterKey: 'testIds', title: 'ID', show: true },
    { filterKey: 'destinations', title: 'Destinations', show: true },
    { filterKey: 'userIds', title: 'Users', show: false },
    { filterKey: 'statuses', title: 'Status', show: true },
    { filterKey: 'callerIds', title: 'Caller', show: false },
    { filterKey: 'testGroupIds', title: 'Test group id', show: false },
    { filterKey: 'date', title: 'Date', show: false },
    { filterKey: 'searchByPhoneNumbers', title: 'Phone', show: false },
  ];

  model: VtSearchModel = {
    testIds: '',
    testGroupIds: '',
    callerIds: '',
    statuses: [],
    networks: [],
    userIds: [],
    users: [],
    createdAtFrom: null,
    createdAtTo: null,
  };

  loading = false;
  user: AuthUser;
  usersModal: DialogRef;

  constructor(
    public userService: UsersService,
    public validationService: ValidationService,
    public localStorage: LocalStorageService,
    public modal: ModalService,
    public exportService: ExportService,
    public notificationService: NotificationService,
    formBuilder: FormBuilder
  ) {
    VOICE_TEST_STATUSES.forEach(_ => this.receiptStatuses.push(_));
    this.form = formBuilder.group({
      testIds: ['', Vld.compose([Vld.digitsAndSpaces(true), Vld.maxLength(255)])],
      testGroupIds: ['', Vld.compose([Vld.digitsAndSpaces(true), Vld.maxLength(255)])],
      callerIds: ['', Vld.compose([Vld.maxLength(255)])],
      phoneNumbers: ['', Vld.compose([Vld.maxLength(255)])],
    });
  }

  ngOnInit() {
    this.loading = true;
    this.userService.getAuthUser().then(user => {
      this.user = user;
      const isAdmin = user.role === Role.ADMIN;
      const isMain = user.role === Role.MAIN;
      const isDeputy = user.role === Role.DEPUTY;
      this.showUsers = isAdmin || isMain || isDeputy || user.showAllResults;
      const existingFilters = this.localStorage.get(`vt_${this.user.id}_filter_state`) || [];
      if (existingFilters.length === this.filters.length) {
        this.filters = existingFilters;
      } else {
        existingFilters.forEach(ef => {
          this.filters
            .filter(_ => _.filterKey === ef.filterKey)
            .forEach(f => {
              f.show = ef.show;
            });
        });
      }
      existingFilters.forEach(ef => {
        this.filters
          .filter(_ => _.filterKey === ef.filterKey)
          .forEach(f => {
            f.show = ef.show;
          });
      });
      this.filters = this.filters.filter(f => {
        if (!this.showUsers && f.filterKey === 'userIds') {
          return false;
        }
        return true;
      });
      this.update();
    });
  }

  onChangeDate(dates) {
    if (dates instanceof Event || typeof dates === 'string') {
      return;
    }
    if (dates?.startStr && dates?.endStr) {
      this.model.createdAtFrom = dates.startStr + '.000Z';
      this.model.createdAtTo = dates.endStr + '.999Z';
      this.onSubmitClick();
    } else {
      this.model.createdAtFrom = null;
      this.model.createdAtTo = null;
    }
  }

  shouldAddFilterBeShown(): boolean {
    const hiddenFilters = this.filters.filter(_ => _.show === false);
    return hiddenFilters.length > 0;
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.filters, event.previousIndex, event.currentIndex);
    this.localStorage.set(`vt_${this.user.id}_filter_state`, this.filters);
  }

  deleteItem(event: CdkDragDrop<string[]>) {
    const indexToHide = event.previousIndex;
    this.filters[indexToHide].show = false;
    this.onClear(this.filters[indexToHide].filterKey);
    this.localStorage.set(`vt_${this.user.id}_filter_state`, this.filters);
  }

  update() {
    this.loading = false;
    const savedUserSearchDetails: VtSearchModel = this.localStorage.get(`vt_user_search_${this.user.id}_search`);
    if (savedUserSearchDetails) {
      this.saveUserIds = true;
      this.populateSavedDetails(savedUserSearchDetails);
    } else {
      this.model.createdAtFrom = '';
      this.model.createdAtTo = '';
      this.receiptStatuses.map(_ => (_.selected = false));
      this.usersCollection = [];
      if (this.datePeriod) {
        this.datePeriod.model = '';
        this.datePeriod.control.patchValue('');
      }
    }
  }

  populateSavedDetails(savedModel: VtSearchModel): void {
    this.model.testIds = savedModel.testIds;
    this.model.testGroupIds = savedModel.testGroupIds;
    this.model.callerIds = savedModel.callerIds;
    this.model.createdAtFrom = savedModel.createdAtFrom;
    this.model.createdAtTo = savedModel.createdAtTo;
    //this.model.phoneNumbers = savedModel.destinations.filter(_ => _.manualNumber).map(_ => _.phone).join('\n');
    this.usersCollection = savedModel.users;
    // Check if Show only my tests is enabled... This means usersCollection has only one id of current logged in user ...
    if (savedModel.userIds[0] === this.user.id) {
      this.onlyMyTests = true;
    }
    this.receiptStatuses.map(recieptstatus => {
      if (savedModel.statuses.includes(recieptstatus.id)) {
        recieptstatus.selected = true;
      }
    });
    setTimeout(() => {
      if (this.datePeriod) {
        this.datePeriod.start = Date.parse(this.model.createdAtFrom) / 1000;
        this.datePeriod.end = Date.parse(this.model.createdAtTo) / 1000;
        this.datePeriod.updateValue();
      }
    }, 200);
  }

  getPopulatedModel(): SearchParams {
    const model: SearchParams = {
      networkDetails: [],
      statuses: [],
      testGroupIds: [],
      testIds: [],
      userIds: [],
      callerIds: [],
      sip: false,
    };
    const rawDestinations = this.countries
      ? this.countries.getSelectedModels().map(_ => {
          if (typeof _.custom !== 'undefined' && _.custom) {
            return { phone: _.phonenumber, manualNumber: true };
          }
          return { mcc: _.mcc, mnc: _.mnc };
        })
      : [];
    const filterMccMncPortedMncMap = this.countries ? this.countries.filterMccMncPortedMncMap : new Map();
    const destinations: any[] = [];
    rawDestinations.forEach(dest => {
      if (dest.mcc && dest.mnc) {
        const key = `${dest.mcc}_${dest.mnc}`;
        const entry = filterMccMncPortedMncMap.get(key);
        if (entry && Array.isArray(entry.specificMncs) && entry.specificMncs.length > 0) {
          entry.specificMncs.forEach(specificMnc => {
            destinations.push({
              mcc: dest.mcc,
              mnc: dest.mnc,
              originalMnc: specificMnc,
            });
          });
        } else {
          destinations.push(dest);
        }
      } else {
        destinations.push(dest);
      }
    });
    model.networkDetails = destinations;

    const phoneNumbers = this.form.get('phoneNumbers').value;
    if (phoneNumbers) {
      phoneNumbers.split('\n').map(_ => {
        const phone = _.trim();
        if (phone.length > 0) {
          model.networkDetails.push({ phone: phone, manualNumber: true });
        }
      });
    }
    model.statuses = this.receiptStatuses.filter(_ => _.selected).map(_ => _.id);
    if (this.model.testIds) {
      model.testIds = this.model.testIds
        .split(' ')
        .map(_ => parseInt(_.trim()))
        .filter(_ => _);
    }
    if (this.model.callerIds) {
      model.callerIds = this.model.callerIds
        .split(' ')
        .map(_ => _.trim())
        .filter(_ => _);
    }
    if (this.model.testGroupIds) {
      model.testGroupIds = this.model.testGroupIds
        .split(' ')
        .map(_ => parseInt(_.trim()))
        .filter(_ => _);
    }
    if (this.model.createdAtFrom) {
      model.createdAtFrom = this.model.createdAtFrom;
    }
    if (this.model.createdAtTo) {
      model.createdAtTo = this.model.createdAtTo;
    }
    model.userIds = this.onlyMyTests ? [this.user.id] : this.usersCollection.map(u => u.id);
    return model;
  }

  onSubmitClick(filterKey: string = null) {
    const searchDetails = Object.assign({}, this.model);
    searchDetails.users = this.usersCollection;
    if (this.saveUserIds) {
      this.localStorage.set(`vt_user_search_${this.user.id}_search`, searchDetails);
    } else {
      this.localStorage.remove(`vt_user_search_${this.user.id}_search`);
    }
    this.onSubmit.emit(this.getPopulatedModel());
    this.handleFilterDropdown(filterKey);
  }

  handleFilterDropdown(filterKey: string = null) {
    const closeDropDownForTheseFilters = ['date', 'commentDate', 'searchByTestIdText', 'searchByPhoneNumbers'];
    if (filterKey && closeDropDownForTheseFilters.includes(filterKey)) {
      this.filterTopBar.nativeElement.click();
    }
  }

  openUsersModal() {
    this.usersModal = this.modal.alert().component(this.usersModalTpl).open();
  }

  onSelectUser(user: BasicUser) {
    this.usersCollection.push(user);
    this.onlyMyTests = false;
    this.onSubmitClick();
  }

  onUnSelectUser(user: BasicUser, event: Event = null) {
    if (event) {
      event.stopPropagation();
    }
    this.usersCollection = this.usersCollection.filter(u => user.id !== u.id);
    this.onSubmitClick();
  }

  onChangeOnlyMyTests() {
    this.onlyMyTests = !this.onlyMyTests;
    if (this.onlyMyTests) {
      this.usersCollection = [];
      this.users?.onClearAll();
    } else {
      this.onUnSelectUser({
        id: this.user.id,
        userId: this.user.id,
        email: this.user.email,
        username: this.user.username,
        fullName: this.user.fullName,
      });
    }
    this.onSubmitClick();
  }

  getPeekForSelectedValues(filter): number | string | null {
    switch (filter.filterKey) {
      case 'destinations':
        return this.countries?.getSelectedModels().length;
      case 'userIds':
        return this.usersCollection.length;
      case 'statuses':
        return this.receiptStatuses.filter(rs => rs.selected).length;
      default:
        return null;
    }
  }

  onClickExport() {
    this.exportSize = 'Custom';
    this.exportSpinner = false;
    this.exportData = {
      url: undefined,
      size: undefined,
      sizeKb: undefined,
    };
    this.modal.alert().dialogClass('modal-dialog small-modal').component(this.exportModalTpl).open();
  }

  export(limit: string | number) {
    const values = new Map<string, number | null>([
      ['All', null],
      ['Custom', this.customExportSize],
    ]);
    const size: number | null = typeof limit === 'string' ? values.get(limit) : limit;
    this.exportSpinner = true;
    const params = this.getPopulatedModel();
    this.exportService
      .exportVoiceTestsResults(size, params)
      .then(exportData => {
        this.exportData = exportData;
        this.exportSpinner = false;
      })
      .catch(error => {
        this.notificationService.error({
          title: 'Manual testing',
          message: 'An error occurred while export tests',
          serviceName: 'MTC',
          requestMessage: error.statusText,
          requestCode: error.status,
          ts: error.timestamp ? error.timestamp : null,
        });
        this.exportSpinner = false;
      });
  }

  onAdditionalFilterClick(filter): void {
    filter.show = !filter.show;
    this.localStorage.set(`vt_${this.user.id}_filter_state`, this.filters);
  }

  onFilterChange(filter): void {
    for (const f of this.filters) {
      if (f.filterKey === filter) {
        f.show = !f.show;
        break;
      }
    }
  }

  onDestinationChange(): void {
    this.onSubmitClick();
  }

  receiptStatusChange(event: any, status: any): void {
    event.stopPropagation();
    status.selected = !status.selected;
    this.onSubmitClick();
  }

  onClearAllReceiptStatus(): void {
    this.receiptStatuses.map(_ => (_.selected = false));
    this.onSubmitClick();
  }

  resetAll(): void {
    this.form.reset();
    this.model.testGroupIds = '';
    this.model.testIds = '';
    this.model.callerIds = '';
    this.model.createdAtFrom = '';
    this.model.createdAtTo = '';
    if (this.datePeriod) {
      this.datePeriod.model = '';
      this.datePeriod.control.patchValue('');
    }
    this.receiptStatuses.map(_ => (_.selected = false));
    this.countries?.reset();
    this.usersCollection = this.onlyMyTests ? this.usersCollection : [];
    this.users?.onClearAll();
    // this.saveUserIds = false;
  }

  onClearAllFilter(): void {
    this.resetAll();
    this.localStorage.set(`vt_${this.user.id}_filter_state`, this.filters);
    this.onSubmitClick();
  }

  onClear(filterKey: string) {
    switch (filterKey) {
      case 'testIds':
        this.model.testIds = '';
        break;
      case 'testGroupIds':
        this.model.testGroupIds = '';
        break;
      case 'callerIds':
        this.model.callerIds = '';
        break;
      case 'date':
        this.onChangeDate(null);
        if (this.datePeriod) {
          this.datePeriod.model = '';
          this.datePeriod.control.patchValue('');
          this.datePeriod.reset();
        }
        this.handleFilterDropdown('date');
        break;
      case 'users':
        this.usersCollection = [];
        break;
      case 'statuses':
        this.receiptStatuses.forEach(rs => (rs.selected = false));
        break;
      case 'searchByPhoneNumbers':
        this.form.get('phoneNumbers').setValue('');
        break;
    }
    this.onSubmitClick();
  }

  isFilterActive(filter): boolean {
    switch (filter.filterKey) {
      case 'destinations':
        return this.countries?.getSelectedModels().length > 0;
      case 'testIds':
        return this.model.testIds?.length > 0;
      case 'testGroupIds':
        return this.model.testGroupIds?.length > 0;
      case 'callerIds':
        return this.model.callerIds?.length > 0;
      case 'date':
        return !!this.model.createdAtFrom || !!this.model.createdAtTo;
      case 'userIds':
        return this.usersCollection.length > 0;
      case 'statuses':
        return this.receiptStatuses.some(rs => rs.selected);
      case 'searchByPhoneNumbers':
        return this.form.get('phoneNumbers').value?.length > 0;
      default:
        return false;
    }
  }

  getIconForStatus(statusID: string): string {
    return CustomUtils.getIconForStatus(statusID);
  }
}
